import React from "react";
import { motion } from "framer-motion";

/**
 * Identified image placeholder.
 * Renders a dark, grainy, labeled box that occupies the real dimensions
 * of the future image. Used site-wide instead of real photos.
 */
export const Placeholder = ({ label, ratio = "16/9", className = "", tone = "dark", parallax = false }) => {
  const isDark = tone === "dark";
  return (
    <div
      className={`relative overflow-hidden rounded-lg grain select-none ${
        isDark ? "bg-ink text-white" : "bg-[#F0F0F0] text-ink"
      } ${className}`}
      style={{ aspectRatio: ratio }}
      data-testid={`placeholder-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      {/* corner ticks */}
      <span className="absolute left-3 top-3 h-3 w-3 border-l-2 border-t-2 border-lime-accent" />
      <span className="absolute right-3 top-3 h-3 w-3 border-r-2 border-t-2 border-lime-accent" />
      <span className="absolute bottom-3 left-3 h-3 w-3 border-b-2 border-l-2 border-lime-accent" />
      <span className="absolute bottom-3 right-3 h-3 w-3 border-b-2 border-r-2 border-lime-accent" />

      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-lime-accent/80">
          image
        </span>
        <span className="font-grotesk text-sm font-bold uppercase tracking-[0.15em] sm:text-base">
          [ {label} ]
        </span>
        <span className={`font-mono text-[10px] ${isDark ? "text-white/40" : "text-ink/40"}`}>
          {ratio.replace("/", ":")}
        </span>
      </div>
    </div>
  );
};

/** Real image framed with the same premium treatment as placeholders. */
export const Media = ({ src, alt = "Boxly Smart Locker", ratio = "16/9", className = "", fit = "cover", bg = "dark" }) => {
  const isDark = bg === "dark";
  const tick = isDark ? "border-lime-accent" : "border-ink";
  return (
    <div
      className={`group relative overflow-hidden rounded-lg ${isDark ? "bg-ink" : "bg-[#F4F4F4]"} ${className}`}
      style={{ aspectRatio: ratio }}
      data-testid="media-image"
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`h-full w-full transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06] ${fit === "contain" ? "object-contain p-5" : "object-cover"}`}
      />
      <span className={`pointer-events-none absolute left-3 top-3 h-3 w-3 border-l-2 border-t-2 ${tick}`} />
      <span className={`pointer-events-none absolute right-3 top-3 h-3 w-3 border-r-2 border-t-2 ${tick}`} />
      <span className={`pointer-events-none absolute bottom-3 left-3 h-3 w-3 border-b-2 border-l-2 ${tick}`} />
      <span className={`pointer-events-none absolute bottom-3 right-3 h-3 w-3 border-b-2 border-r-2 ${tick}`} />
    </div>
  );
};

/** Scroll reveal wrapper (fade + slide up) with stagger support. */
export const Reveal = ({ children, delay = 0, y = 28, className = "", once = true }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

/** Masked line-by-line reveal for large display headings (animates on mount). */
export const MaskedLines = ({ lines, className = "", lineClassName = "" }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.05em]">
        <motion.span
          className={`block ${lineClassName}`}
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 0.85, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

/** Small uppercase section label with a lime tick. */
export const SectionLabel = ({ children, dark = false }) => (
  <div className="mb-6 flex items-center gap-3" data-testid="section-label">
    <span className="h-2 w-2 bg-lime-accent" />
    <span className={`font-mono text-xs font-medium uppercase tracking-[0.3em] ${dark ? "text-white/70" : "text-ink/60"}`}>
      {children}
    </span>
  </div>
);
